import { all, put, call, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import diff from 'object-diff';
import ApiService from '@/services/Api';
import config from '@/services/Api/apiConfig';
import actions, { actionTypes } from './actions';
import leadActions from '../lead/actions';
import modalsActions from '../modals/actions';
import { formatRequestDate } from '@/utils/redux';
import { message } from 'antd';

function* fetchProposals({ payload }) {
  try {
    const { data } = yield call(ApiService.fetchProposals, payload);
    yield put(actions.fetchSuccess(data));
  } catch (error) {
    yield put(actions.fetchFailed(error));
  }
}

function* fetchProposal({ payload }) {
  const { proposalId, pricingModelType } = payload;
  try {
    const { data } = yield call(ApiService.fetchProposal, proposalId, pricingModelType);
    yield put(actions.fetchProposalSuccess(data));
  } catch (error) {
    yield put(actions.fetchProposalFailed(error));
  }
}

function* createProposal({ payload }) {
  const hideMessage = message.info('Request initiated. Please wait.', 5);
  try {
    const { data } = yield call(ApiService.createProposal, {
      lead: payload.leadId,
      proposal_type: payload.proposalType,
      need_customization: payload.needCustomization,
      ...payload.values,
      survey_scheduled_date: formatRequestDate(payload.values?.survey_scheduled_date),
    });
    yield put(actions.createProposalSuccess(data));
    yield put(actions.setLoadingPercent(0));
    yield put(actions.clearCreateErrors());
    yield put(modalsActions.toggleProposalsFormModal(false));
    yield put(leadActions.fetchLead(payload.leadId));
  } catch (error) {
    hideMessage();
    yield put(actions.createProposalFailed(error));
    yield put(actions.setLoadingPercent(0));
    yield put(modalsActions.toggleProposalsFormModal(false));
  }
}

function* editProposal({ payload }) {
  const { proposalId, initialValues, values, pricingModelType } = payload;
  const delta = diff(initialValues, values);

  const { rebate_pay_date, est_solar_pis_date, est_assessment_filing_date, pis_date, ...rest } = delta;

  const requestData = {
    ...rest,
    pricing_model_type: pricingModelType,
    est_solar_pis_date: formatRequestDate(est_solar_pis_date),
    est_assessment_filing_date: formatRequestDate(est_assessment_filing_date),
  };

  try {
    const { data } = yield call(ApiService.editProposal, proposalId, requestData);
    yield put(actions.editProposalSuccess(data));
    yield put(leadActions.fetchLead(payload.leadId));
  } catch (error) {
    yield put(actions.editProposalFailed(error));
  }
}

function* generateProposal({ payload }) {
  try {
    const { data } = yield call(ApiService.generateProposal, {
      proposal_pk: payload.id,
    });
    yield put(actions.generateProposalSuccess(data));
    yield put(leadActions.fetchLead(payload.leadId));
    yield put(actions.setLoadingPercent(0));
  } catch (error) {
    yield put(actions.generateProposalFailed(error));
    yield put(actions.setLoadingPercent(0));
  }
}

function* uploadCustomTemplate({ payload }) {
  const { file, token, proposalId, leadId } = payload;
  const formData = new FormData();
  const requestUrl = `${config.BASE_URI}/api/proposal/${proposalId}`;
  formData.append('pdf_file', file);

  try {
    const { data } = yield call(axios, {
      method: 'patch',
      url: requestUrl,
      data: formData,
      headers: {
        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
        Authorization: `Token ${token}`,
      },
    });

    yield put(actions.editProposalSuccess(data));
    yield put(modalsActions.toggleEditProposalsTableModal(false));
    yield put(leadActions.fetchLead(leadId));
  } catch (error) {
    yield put(actions.editProposalFailed(error));
  }
}

export default function* () {
  yield all([
    takeLatest(actionTypes.FETCH_PROPOSALS, fetchProposals),
    takeLatest(actionTypes.FETCH_PROPOSAL, fetchProposal),
    takeLatest(actionTypes.CREATE_PROPOSAL, createProposal),
    takeLatest(actionTypes.EDIT_PROPOSAL, editProposal),
    takeLatest(actionTypes.GENERATE_PROPOSAL, generateProposal),
    takeLatest(actionTypes.UPLOAD_CUSTOM_TEMPLATE, uploadCustomTemplate),
  ]);
}
